import React, { useEffect, useState } from "react";
import styles from '../css/css_pages/InvoiceManagement.module.css';
import Cookies from "js-cookie";
import config from "../../config";
import { useTranslation } from 'react-i18next';
import { FiX, FiCheckCircle } from "react-icons/fi";

const ClientFinance = () => {
  const { t } = useTranslation();
  const [allInvoices, setAllInvoices] = useState([]);
  const [activeInvoices, setActiveInvoices] = useState([]);
  const [completedInvoices, setCompletedInvoices] = useState([]);
  const [showCompletedInvoices, setShowCompletedInvoices] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    // Separate invoices into active and completed whenever allInvoices changes
    const active = allInvoices.filter(invoice => parseFloat(invoice.balance) > 0);
    const completed = allInvoices.filter(invoice => parseFloat(invoice.balance) === 0);
    
    setActiveInvoices(active);
    setCompletedInvoices(completed);
  }, [allInvoices]);

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);
    
    // Format as UTC time
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'  // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
    }

    return date.toLocaleString('en-US', options).replace(',', '');
  };

  const formatCurrency = (amount) => {
    return `Rs.${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const fetchInvoices = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t('auth_error'));
      }

      const response = await fetch(`${config.API_URL}/client/list-invoices`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('fetch_error'));
      }

      const data = await response.json();
      console.log('Fetched Invoices:', data);
      setAllInvoices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(t('fetch_error_details'), error);
      setAllInvoices([]);
    }
  };

  const fetchPaymentHistory = async (invoiceId) => {
    try {
      setIsLoadingPayments(true);
      setPaymentHistoryError(null);
      setPaymentHistory([]);

      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t('auth_error'));
      }

      console.log(`Fetching payment history for invoice ID: ${invoiceId}`);

      const response = await fetch(`${config.API_URL}/client/get-all-payments/${invoiceId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('fetch_error'));
      }

      const result = await response.json();
      console.log('API Response:', result);

      let paymentData = [];

      if (Array.isArray(result)) {
        paymentData = result;
      } else if (result && Array.isArray(result.response)) {
        paymentData = result.response;
      } else if (result && Array.isArray(result.data)) {
        paymentData = result.data;
      } else if (result && result.response && Array.isArray(result.response.data)) {
        paymentData = result.response.data;
      }

      console.log('Extracted Payment Data:', paymentData);
      setPaymentHistory(paymentData);

    } catch (error) {
      console.error('Error fetching payment history:', error);
      setPaymentHistoryError(error.message || 'Failed to load payment history');
      setPaymentHistory([]);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleViewPaymentHistory = (invoice) => {
    setCurrentInvoice(invoice);
    setShowPaymentHistoryModal(true);
    fetchPaymentHistory(invoice.invoice_id);
  };

  const handleCloseModal = () => {
    setShowPaymentHistoryModal(false);
    setCurrentInvoice(null);
    setPaymentHistory([]);
    setPaymentHistoryError(null);
  };

  const toggleCompletedInvoices = () => {
    setShowCompletedInvoices(!showCompletedInvoices);
  };

  const renderInvoicesTable = (invoices) => {
    return (
      <table className={styles.invoicesTable}>
        <thead>
          <tr>
            <th>{t('invoice')}</th>
            <th>{t('type')}</th>
            <th>{t('description')}</th>
            <th>{t('amount')}</th>
            <th>{t('balance')}</th>
            <th>{t('due_date')}</th>
            <th>{t('invoice_date')}</th>
            {showCompletedInvoices && <th>{t('status')}</th>}
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice, index) => (
            <tr
              key={index}
              className={styles.clickableRow}
              onClick={() => handleViewPaymentHistory(invoice)}
            >
              <td className={styles.numberCell}>{invoice.invoice_no}</td>
              <td className={styles.typeCell}>
                <span className={`${styles.typeBadge} ${styles[invoice.invoice_type.toLowerCase()]}`}>
                  {invoice.invoice_type}
                </span>
              </td>
              <td className={styles.clientCell}>
                <div className={styles.clientInfo}>
                  <div className={styles.clientName}>{invoice.description || t('not_provided')}</div>
                </div>
              </td>
              <td className={styles.amountCell}>{formatCurrency(invoice.amount)}</td>
              <td className={`${styles.amountCell} ${parseFloat(invoice.balance) === 0 ? styles.zeroBalance : ''}`}>
                {formatCurrency(invoice.balance)}
              </td>
              <td className={styles.dateCell}>{formatDateTime(invoice.due_date, false)}</td>
              <td className={styles.dateCell}>{formatDateTime(invoice.created_date, false)}</td>
              {showCompletedInvoices && (
                <td className={styles.statusCell}>
                  {parseFloat(invoice.balance) === 0 ? (
                    <span className={styles.completedBadge}>
                      <FiCheckCircle /> {t('paid')}
                    </span>
                  ) : (
                    <span className={styles.pendingBadge}>{t('pending')}</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className={styles.invoiceManagement}>
      <div className={styles.headerTitleRow}>
        <h1>{t('client_finance')}</h1>
        <button 
          className={`${styles.completedToggle} ${showCompletedInvoices ? styles.active : ''}`}
          onClick={toggleCompletedInvoices}
        >
          <FiCheckCircle /> {showCompletedInvoices ? t('show_active_invoices') : t('show_completed_invoices')}
        </button>
      </div>
      
      <div className={styles.invoicesContainer}>
        <div className={styles.tableWrapper}>
          {showCompletedInvoices ? (
            completedInvoices.length > 0 ? (
              renderInvoicesTable(completedInvoices)
            ) : (
              <p className={styles.noResults}>{t('no_completed_invoices')}</p>
            )
          ) : activeInvoices.length > 0 ? (
            renderInvoicesTable(activeInvoices)
          ) : (
            <p className={styles.noResults}>{t('no_active_invoices')}</p>
          )}
        </div>
      </div>

      {/* Payment History Modal */}
      {showPaymentHistoryModal && currentInvoice && (
        <div className={styles.modalOverlay} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className={styles.paymentHistoryModal} style={{ width: '90%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className={styles.modalHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
              <h2>{t('payment_history')} ({currentInvoice.invoice_no})</h2>
              <button className={styles.closeButton} onClick={handleCloseModal} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '1.5rem', cursor: 'pointer' }}>
                <FiX />
              </button>
            </div>

            <div className={styles.modalScrollContent} style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
              <div className={styles.invoiceSummary} style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                <div className={styles.summaryRow} style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}>
                  <div className={styles.summaryItem} style={{ flex: 1, padding: '0 0.5rem', marginBottom: '0.5rem' }}>
                    <span className={styles.summaryLabel} style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>{t('description')}:</span>
                    <span className={styles.summaryValue} style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.875rem' }}>
                      {currentInvoice.description || t('not_provided')}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryRow} style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}>
                  <div className={styles.summaryItem} style={{ flex: 1, padding: '0 0.5rem', marginBottom: '0.5rem' }}>
                    <span className={styles.summaryLabel} style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>{t('created_date')}:</span>
                    <span className={styles.summaryValue} style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.875rem' }}>
                      {formatDateTime(currentInvoice.created_date, false)}
                    </span>
                  </div>
                  <div className={styles.summaryItem} style={{ flex: 1, padding: '0 0.5rem', marginBottom: '0.5rem' }}>
                    <span className={styles.summaryLabel} style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>{t('invoice_amount')}:</span>
                    <span className={styles.summaryValue} style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.875rem' }}>
                      {formatCurrency(currentInvoice.amount)}
                    </span>
                  </div>
                </div>
              </div>

              {isLoadingPayments ? (
                <div className={styles.loadingPayments} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 0', minHeight: '200px' }}>
                  <div className={styles.loadingSpinner} />
                  <p>{t('loading_payment_history')}</p>
                </div>
              ) : paymentHistoryError ? (
                <div className={styles.paymentHistoryError} style={{ color: '#dc2626', textAlign: 'center', padding: '1rem', fontSize: '0.875rem', minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <p>{t('error_loading_payments')}: {paymentHistoryError}</p>
                  <button
                    className={styles.retryButton}
                    onClick={() => fetchPaymentHistory(currentInvoice.invoice_id)}
                    style={{ backgroundColor: '#4338ca', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    {t('retry')}
                  </button>
                </div>
              ) : paymentHistory.length > 0 ? (
                <div className={styles.paymentHistoryTableWrapper} style={{ maxHeight: '40vh', overflowY: 'auto', marginBottom: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table className={styles.paymentHistoryTable} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr>
                        <th style={{ background: '#f1f5f9', padding: '0.5rem', textAlign: 'left', color: '#64748b', position: 'sticky', top: 0, zIndex: 1, fontSize: '0.7rem' }}>{t('date')}</th>
                        <th style={{ background: '#f1f5f9', padding: '0.5rem', textAlign: 'left', color: '#64748b', position: 'sticky', top: 0, zIndex: 1, fontSize: '0.7rem' }}>{t('description')}</th>
                        <th style={{ background: '#f1f5f9', padding: '0.5rem', textAlign: 'left', color: '#64748b', position: 'sticky', top: 0, zIndex: 1, fontSize: '0.7rem' }}>{t('amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment) => (
                        <tr key={payment.payment_id}>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', wordBreak: 'break-word' }}>{formatDateTime(payment.payment_date, false)}</td>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', wordBreak: 'break-word' }}>{payment.payment_description || 'N/A'}</td>
                          <td className={styles.paymentAmount} style={{ padding: '0.5rem', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', wordBreak: 'break-word', fontWeight: 500, color: '#16a34a' }}>
                            {formatCurrency(payment.payment_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noPayments} style={{ textAlign: 'center', padding: '2rem 1rem', color: '#64748b', fontSize: '0.875rem', minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <p>{t('no_payment_history')}</p>
                </div>
              )}

              <div className={styles.currentBalanceSection} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '8px', marginTop: '1rem' }}>
                <div className={styles.balanceLabel} style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>{t('current_balance')}:</div>
                <div className={styles.balanceAmount} style={{ fontSize: '1rem', fontWeight: 700, color: parseFloat(currentInvoice.balance) === 0 ? '#16a34a' : '#1e40af' }}>
                  {formatCurrency(currentInvoice.balance)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientFinance;